import { useMemo } from 'react';
import {
  Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis,
} from 'recharts';
import { SectionCard, fmtNum, fmtTRY } from '../yeni-design/primitives';
import { demoTheme } from '../../theme';
import { kanalSeyri } from './analizHesaplari';
import { BlokIskeleti } from './BlokIskeleti';
import { CUBUK_ETIKET, cubukBoyu } from './cubukBoyu';
import type { Donem, SatisDonemi } from './donem';
import type { SaKaynak } from './ekranKaynaklari';
import { kanalRengi } from './kanalRenk';
import type { KanalSatiri } from './ozetHesaplari';
import { useSaSorgu } from './useSatisVerisi';

interface Props {
  kaynak: SaKaynak | undefined;
  donem: Donem;
  donemTuru?: SatisDonemi;
  sube?: string | null;
  kanal?: string | null;
}

/** Kanal sekmesi : kanal grubu bazinda ciro seyri (990040, ek istek yok). Tek gunde seyir yok, cizilmez. */
export function KanalSeyirBlok({ kaynak, donem, donemTuru = 'ozel', sube = null, kanal = null }: Props) {
  const q = useSaSorgu<KanalSatiri>(kaynak, { donem, sube: null, kanal: null }, !donem.tekGun);
  const boy = cubukBoyu(donemTuru, { bas: donem.bas, bit: donem.bit }).varsayilan;
  const seyir = useMemo(
    () => kanalSeyri(q.data?.data ?? [], sube, kanal, boy, { bas: donem.bas, bit: donem.bit }),
    [q.data, sube, kanal, boy, donem.bas, donem.bit],
  );

  if (!kaynak || donem.tekGun) return null;
  if (q.isLoading) return <BlokIskeleti testid="blok-kanal-seyir-iskelet" baslik="Kanal seyri" yukseklik={240} />;
  if (seyir.noktalar.length < 2) return null;

  return (
    <div data-testid="blok-kanal-seyir">
      <SectionCard title="Kanal seyri" subtitle={`${CUBUK_ETIKET[boy]} bazında net ciro`}>
        <div className="h-[240px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={seyir.noktalar} margin={{ top: 4, right: 12, left: 4, bottom: 4 }}>
              <CartesianGrid strokeDasharray="2 4" stroke={demoTheme.border} vertical={false} />
              <XAxis
                dataKey="etiket"
                tick={{ fontSize: 11, fill: demoTheme.textMuted }}
                axisLine={{ stroke: demoTheme.border }}
                tickLine={false}
                minTickGap={24}
                interval="preserveStartEnd"
              />
              <YAxis
                tickFormatter={(v) => fmtNum(Number(v))}
                tick={{ fontSize: 11, fill: demoTheme.textMuted }}
                axisLine={false}
                tickLine={false}
                width={64}
              />
              <ChartTooltip formatter={(v) => fmtTRY(Number(v))} />
              <Legend iconType="square" iconSize={10} wrapperStyle={{ fontSize: 12 }} />
              {seyir.gruplar.map((g) => (
                <Bar key={g} dataKey={g} stackId="kanal" fill={kanalRengi(g)} isAnimationActive={false} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </SectionCard>
    </div>
  );
}
